import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import moment from "moment";
import { pdfjs } from "react-pdf";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { Button as PrimaryButton } from "../Common/styles.common";
import Datepicker, { MONTHS } from "../../shared/Datepicker/Datepicker";
import Dropdown from "../../shared/Dropdown/Dropdown";
import ShowToast from "../Common/Toast";
import useVendorStore from "../HomePage/useVendorStore";
import { downloadBlob } from "operations/operations";
import styles from "./DownloadPackagingLabel.module.scss";

const DATE_FORMAT = "YYYY-MM-DD";
const BATCH_DATE_FORMAT = "YYYYMMDD";

const FORMAT_PDF = "pdf";
const FORMAT_PNG = "png";
const QUESTION_ANSWERS_STORAGE_PREFIX = "packing_label_question_answers";
const FORMAT_OPTIONS = [
  { value: FORMAT_PDF, label: "PDF" },
  { value: FORMAT_PNG, label: "PNG" },
];

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const LABEL_SIZE_OPTIONS = [
  { value: "50X50", label: "50mm x 50mm" },
  { value: "75X50", label: "75mm x 50mm" },
  { value: "100X50", label: "100mm x 50mm" },
];

const FloatingDateInput = forwardRef(({ value, onClick, label }, ref) => {
  const filled = !!value;
  return (
    <div
      ref={ref}
      className={`${styles.fieldBox} ${filled ? styles.floating : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.(e);
      }}
    >
      <span className={styles.fieldLabel}>{label}</span>
      {filled ? <span className={styles.fieldValue}>{value}</span> : null}
      <span className={styles.iconRight} aria-hidden>
        <Calendar size={20} strokeWidth={1.6} />
      </span>
    </div>
  );
});
FloatingDateInput.displayName = "FloatingDateInput";

const FloatingSelect = ({ value, onChange, options, label, disabled }) => {
  const filled = value !== "" && value !== null && value !== undefined;
  const selected = options?.find((o) => String(o?.value) === String(value));
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className={styles.selectWrapper} ref={wrapRef}>
      <div
        className={`${styles.fieldBox} ${filled ? styles.floating : ""} ${open ? styles.focused : ""} ${disabled ? styles.fieldBoxDisabled : ""}`}
        onClick={() => { if (!disabled) setOpen((p) => !p); }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={!!disabled}
      >
        {filled ? (
          <React.Fragment>
            <span className={styles.fieldLabel}>{label}</span>
            <span className={styles.fieldValue}>{selected?.label}</span>
          </React.Fragment>
        ) : (
          <span className={styles.fieldPlaceholder}>{label}</span>
        )}
        <span className={`${styles.chevronIcon} ${open ? styles.chevronOpen : ""}`} aria-hidden>
          <ChevronDown size={14} strokeWidth={1.8} />
        </span>
      </div>
      {open && !disabled && (
        <ul className={styles.dropdownMenu} role="listbox">
          {options?.map((o) => (
            <li
              key={`${o?.value}`}
              className={`${styles.dropdownItem} ${String(o?.value) === String(value) ? styles.dropdownItemSelected : ""}`}
              role="option"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(o?.value);
                setOpen(false);
              }}
            >
              {o?.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const YesNoField = ({ label, value, onChange }) => (
  <div className={styles.toggleRow}>
    <span className={styles.toggleLabel}>{label}</span>
    <div className={styles.toggleOptions}>
      {[
        { v: true, l: "Yes" },
        { v: false, l: "No" },
      ].map((opt) => (
        <button
          key={opt?.l}
          type="button"
          className={`${styles.toggleOption} ${value === opt?.v ? styles.toggleOptionActive : ""}`}
          onClick={() => onChange?.(opt?.v)}
        >
          {opt?.l}
          <span className={styles.radioDot}>
            <span className={value === opt?.v ? styles.radioDotInner : ""} />
          </span>
        </button>
      ))}
    </div>
  </div>
);

const fetchLabelBytes = async (url) => {
  const response = await fetch(url, { method: "GET", mode: "cors" });
  if (!response.ok) {
    throw new Error(`Failed to download label (${response.status})`);
  }
  return response.arrayBuffer();
};

const PDF_POINT_DPI = 72;
const PNG_TARGET_DPI = 600;

const convertPdfToPng = async (pdfBytes) => {
  const pdf = await pdfjs.getDocument({ data: pdfBytes }).promise;
  try {
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: PNG_TARGET_DPI / PDF_POINT_DPI });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not prepare PNG download");

    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await page.render({ canvasContext: context, viewport }).promise;

    const pngBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error("Could not create PNG label")),
        "image/png"
      );
    });
    return pngBlob.arrayBuffer();
  } finally {
    await pdf.destroy();
  }
};

const downloadLabel = async ({ url, format, fileName }) => {
  const pdfBytes = await fetchLabelBytes(url);
  if (format === FORMAT_PNG) {
    const pngBytes = await convertPdfToPng(pdfBytes);
    downloadBlob(pngBytes, `${fileName}.png`, "image/png");
    return;
  }
  downloadBlob(pdfBytes, `${fileName}.pdf`, "application/pdf");
};

const getStoredQuestionAnswers = (storageKey) => {
  if (!storageKey) return {};
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey) || "{}");
    return stored && typeof stored === "object" ? stored : {};
  } catch {
    return {};
  }
};

const getStoredBoolean = (value) =>
  typeof value === "boolean" ? value : null;

const DownloadPackagingLabel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const outletId = window.localStorage.getItem("outlet_id");
  const vendorId = Number(outletId);
  const printPackagingLabel = useVendorStore((s) => s.printPackagingLabel);

  const product = location?.state?.product || null;
  const requirements = location?.state?.requirements || {};
  const returnTo = location?.state?.returnTo || "/download-labels";
  const showFssaiToggle = !!requirements?.fssai_logo;
  const showOrganicToggle = !!requirements?.organic_logo;
  const showWaxToggle = !!requirements?.wax_coating;
  const variants = product?.active_product_variants || [];
  const questionAnswersStorageKey =
    outletId && product?.product_number
      ? `${QUESTION_ANSWERS_STORAGE_PREFIX}:${outletId}:${product?.product_number}`
      : "";
  const storedQuestionAnswers = useMemo(
    () => getStoredQuestionAnswers(questionAnswersStorageKey),
    [questionAnswersStorageKey]
  );

  const variantOptions = useMemo(
    () =>
      variants
        .map((v, idx) => ({
          value: idx,
          label: `${v?.upc}: ${v?.external_variant_description}`,
        }))
        .filter((o) => !!o?.label?.trim()),
    [variants]
  );

  const [selectedVariantIdx, setSelectedVariantIdx] = useState(
    variantOptions?.length === 1 ? variantOptions?.[0]?.value : ""
  );
  const [packedOnDate, setPackedOnDate] = useState(() =>
    moment().startOf("day").toDate()
  );
  const [batchNumber, setBatchNumber] = useState("");
  const [viewDate, setViewDate] = useState(() => new Date());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fssaiPrinted, setFssaiPrinted] = useState(() =>
    getStoredBoolean(storedQuestionAnswers?.fssai_printed)
  );
  const [foodTypePrinted, setFoodTypePrinted] = useState(() =>
    getStoredBoolean(storedQuestionAnswers?.food_type_printed)
  );
  const [isOrganic, setIsOrganic] = useState(() =>
    getStoredBoolean(storedQuestionAnswers?.is_organic)
  );
  const [isWaxCoated, setIsWaxCoated] = useState(() =>
    getStoredBoolean(storedQuestionAnswers?.is_wax_coated)
  );
  const [labelSize, setLabelSize] = useState(LABEL_SIZE_OPTIONS?.[0]?.value);
  const [downloadFormat, setDownloadFormat] = useState(FORMAT_PDF);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // The app disables document scroll globally (html, body { overflow: hidden }).
  // Re-enable it while this (taller-than-viewport) screen is mounted.
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  const selectedVariant =
    selectedVariantIdx === "" ? null : variants[Number(selectedVariantIdx)];
  const productVariantId = Number(
    selectedVariant?.product_variant_mapping_id
  );
  const hasValidProductVariantId =
    Number.isInteger(productVariantId) && productVariantId > 0;

  const { packingMinDate, packingMaxDate } = useMemo(() => {
    const today = moment().startOf("day");
    const shelfLifeDays = Number(product?.shelf_life) || 0;
    return {
      packingMinDate:
        shelfLifeDays >= 0
          ? today.clone().subtract(Math.max(0, shelfLifeDays - 1), "days").toDate()
          : undefined,
      packingMaxDate: today.clone().add(1, "day").toDate(),
    };
  }, [product?.shelf_life]);

  const yearOptions = useMemo(() => {
    const minYear = packingMinDate ? packingMinDate.getFullYear() : new Date().getFullYear() - 5;
    const maxYear = packingMaxDate ? packingMaxDate.getFullYear() : new Date().getFullYear();
    const years = [];
    for (let y = minYear; y <= maxYear; y += 1) years.push({ value: y, label: String(y) });
    return years;
  }, [packingMinDate, packingMaxDate]);

  const monthOptions = useMemo(() => MONTHS.map((m, idx) => ({ value: idx, label: m })), []);

  const generatedBatchNumber = useMemo(() => {
    const shelfLifeDays = Number(product?.shelf_life) || 0;
    if (
      !packedOnDate ||
      !Number.isInteger(vendorId) ||
      vendorId <= 0 ||
      !product?.product_number ||
      shelfLifeDays <= 0
    ) {
      return "";
    }

    const expiryDate = moment(
      moment(packedOnDate).format(DATE_FORMAT),
      DATE_FORMAT
    )
      .add(shelfLifeDays - 1, "days")
      .format(BATCH_DATE_FORMAT);

    return `B${vendorId}P${product?.product_number}E${expiryDate}`;
  }, [packedOnDate, product?.product_number, product?.shelf_life, vendorId]);

  useEffect(() => {
    setBatchNumber(generatedBatchNumber);
  }, [generatedBatchNumber]);

  useEffect(() => {
    if (!questionAnswersStorageKey) return;
    window.localStorage.setItem(
      questionAnswersStorageKey,
      JSON.stringify({
        fssai_printed: fssaiPrinted,
        food_type_printed: foodTypePrinted,
        is_organic: isOrganic,
        is_wax_coated: isWaxCoated,
      })
    );
  }, [
    questionAnswersStorageKey,
    fssaiPrinted,
    foodTypePrinted,
    isOrganic,
    isWaxCoated,
  ]);

  const isValid =
    !!selectedVariant?.upc &&
    hasValidProductVariantId &&
    !!packedOnDate &&
    !!batchNumber.trim() &&
    foodTypePrinted !== null &&
    (!showFssaiToggle || fssaiPrinted !== null) &&
    (!showOrganicToggle || isOrganic !== null) &&
    (!showWaxToggle || isWaxCoated !== null) &&
    !!labelSize;

  const handleDownload = () => {
    if (submitting) return;
    if (!hasValidProductVariantId) {
      setError("Selected product variant is invalid. Please select another variant.");
      return;
    }
    if (!isValid) return;
    setSubmitting(true);
    setError("");
    printPackagingLabel(
      {
        vendor_id: vendorId,
        product_number: product?.product_number,
        product_variant_id: productVariantId,
        batch_number: batchNumber.trim(),
        packaging_date: moment(packedOnDate).format(DATE_FORMAT),
        label_size: labelSize,
        skip_fssai_logo: showFssaiToggle ? fssaiPrinted === true : true,
        skip_food_type_logo: foodTypePrinted === true,
        skip_organic_logo: showOrganicToggle ? isOrganic === false : true,
        skip_wax_declaration: showWaxToggle ? isWaxCoated === false : true,
        format: downloadFormat,
      },
      async (data) => {
        if (!data?.label_url) {
          setSubmitting(false);
          setError("No label URL returned. Please try again.");
          return;
        }

        try {
          const fileName = `packaging_label_${vendorId}_${product?.product_number}_${batchNumber.trim()}`
            .replace(/[^a-zA-Z0-9_-]+/g, "_");
          await downloadLabel({
            url: data?.label_url,
            format: downloadFormat,
            fileName,
          });
        } catch (downloadError) {
          setError(
            downloadError?.message || "Failed to download label. Please try again."
          );
        } finally {
          setSubmitting(false);
        }
      },
      (errMsg) => {
        setSubmitting(false);
        setError(errMsg || "Failed to generate label. Please try again.");
      }
    );
  };

  if (!product) {
    return (
      <React.Fragment>
        <Header showLogout={false} showProfile={true} />
        <div className={styles.container}>
          <div className={styles.emptyState}>
            <span>Product not found. Please go back and select a product.</span>
            <PrimaryButton className={`${styles.submitBtn} text-white`} onClick={() => navigate(returnTo, { replace: true })}>
              Back to products
            </PrimaryButton>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header showLogout={false} showProfile={true} />
      <div className={styles.container}>
        <div className={styles.titleRow}>
          <button
            className={styles.backBtn}
            onClick={() => navigate(returnTo, { replace: true })}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5m0 0l6 6m-6-6l6-6" stroke="#1c1c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className={styles.pageTitle}>Download packaging labels</h1>
        </div>

        <div className={styles.card}>
          <span className={styles.cardTitle}>Product details</span>

          {variantOptions?.length > 0 && (
            <FloatingSelect
              value={selectedVariantIdx}
              onChange={(v) => setSelectedVariantIdx(Number(v))}
              options={variantOptions}
              label="Variant"
              disabled={variantOptions?.length === 1}
            />
          )}

          <Datepicker
            wrapperClassName="datepickerFullWidth"
            key={`packing-date-${viewDate.getFullYear()}-${viewDate.getMonth()}`}
            dateFormat="MMMM d, yyyy"
            showMonthDropdown={false}
            showYearDropdown={false}
            selected={packedOnDate}
            openToDate={viewDate}
            open={pickerOpen}
            onInputClick={() => setPickerOpen(true)}
            onClickOutside={() => setPickerOpen(false)}
            onChange={(val) => {
              setPackedOnDate(val);
              setPickerOpen(false);
            }}
            minDate={packingMinDate}
            maxDate={packingMaxDate}
            customInput={<FloatingDateInput label="Packing date" />}
            renderCustomHeader={({ date, changeYear, changeMonth }) => (
              <div className={styles.calHeader}>
                <Dropdown
                  ariaLabel="Select month"
                  value={date.getMonth()}
                  options={monthOptions}
                  onChange={(v) => {
                    const m = Number(v);
                    const next = new Date(viewDate);
                    next.setMonth(m);
                    setViewDate(next);
                    changeMonth(m);
                  }}
                />
                <Dropdown
                  ariaLabel="Select year"
                  value={date.getFullYear()}
                  options={yearOptions}
                  onChange={(v) => {
                    const y = Number(v);
                    const next = new Date(viewDate);
                    next.setFullYear(y);
                    setViewDate(next);
                    changeYear(y);
                  }}
                />
              </div>
            )}
          />

          <div className={`${styles.fieldBox} ${batchNumber ? styles.floating : ""}`}>
            {batchNumber ? <span className={styles.fieldLabel}>Batch number</span> : null}
            <input
              className={styles.fieldInput}
              value={batchNumber}
              placeholder="Batch number"
              onChange={(e) => setBatchNumber(e.target.value)}
            />
          </div>

          {showOrganicToggle && (
            <YesNoField label="Is the food organic?" value={isOrganic} onChange={setIsOrganic} />
          )}
          {showWaxToggle && (
            <YesNoField label="Is there wax coating on the food items?" value={isWaxCoated} onChange={setIsWaxCoated} />
          )}
        </div>

        <div className={styles.card}>
          <span className={styles.cardTitle}>Label configurations</span>
          {showFssaiToggle && (
            <YesNoField label="Is FSSAI logo already printed on your sticker?" value={fssaiPrinted} onChange={setFssaiPrinted} />
          )}
          <YesNoField label="Is Veg/Non-veg logo pre-printed on the sticker?" value={foodTypePrinted} onChange={setFoodTypePrinted} />
          <FloatingSelect value={labelSize} onChange={setLabelSize} options={LABEL_SIZE_OPTIONS} label="Label size" />

          <span className={styles.formatHeading}>Select file format for download</span>
          <div className={styles.formatRow}>
            {FORMAT_OPTIONS.map((opt) => (
              <button
                key={opt?.value}
                type="button"
                className={`${styles.formatOption} ${downloadFormat === opt?.value ? styles.formatOptionActive : ""}`}
                onClick={() => setDownloadFormat(opt?.value)}
              >
                {opt?.label}
                <span className={styles.radioDot}>
                  <span className={downloadFormat === opt?.value ? styles.radioDotInner : ""} />
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.footerBar}>
        <PrimaryButton
          className={`${styles.submitBtn} ${!isValid || submitting ? styles.submitBtnDisabled : ""} text-white`}
          onClick={handleDownload}
          disabled={!isValid || submitting}
        >
          {submitting ? "Generating..." : "Download"}
        </PrimaryButton>
      </div>

      <Footer />

      {error && (
        <ShowToast
          errorMsg={error}
          type="error"
          onClose={() => setError("")}
        />
      )}
    </React.Fragment>
  );
};

export default DownloadPackagingLabel;
